import React from 'react';
import { Alert, Box, Button, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { isMedicationAdministrationResolved, MEDICATION_NOT_TAKEN_REASONS } from './medicationAdministrationOptions';

const formatTime = (occurrence) => {
  if (occurrence.scheduledTime) return occurrence.scheduledTime;
  if (!occurrence.scheduledFor) return '';
  return occurrence.scheduledFor.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
};

const statusLabel = (occurrence) => {
  if (occurrence.status === 'taken') return 'Taken';
  if (occurrence.status === 'missed') return 'Missed';
  if (occurrence.status === 'not_taken') return 'Not taken';
  return 'Needs confirmation';
};

const statusColor = (occurrence) => {
  if (occurrence.status === 'taken') return '#187A63';
  if (occurrence.status === 'missed' || occurrence.status === 'not_taken') return '#8A542E';
  return 'text.secondary';
};

const MedicationOccurrenceRow = ({ occurrence, focused, onRecord, busy }) => {
  const [showNotTaken, setShowNotTaken] = React.useState(false);
  const [reason, setReason] = React.useState('');
  const [note, setNote] = React.useState('');
  const medication = occurrence.medicationSnapshot || {};
  const dose = [medication.dose, medication.unit].filter(Boolean).join(' ');
  const resolved = isMedicationAdministrationResolved(occurrence.status);

  return (
    <Box
      id={`medication-occurrence-${occurrence.id}`}
      sx={{
        px: 1.4,
        py: 1.25,
        borderRadius: 2.5,
        border: '1px solid',
        borderColor: focused ? 'primary.main' : occurrence.status === 'missed' || occurrence.status === 'not_taken' ? '#F0C9AA' : '#DCE5E1',
        bgcolor: occurrence.status === 'taken' ? '#EFFAF6' : occurrence.status === 'missed' || occurrence.status === 'not_taken' ? '#FFF5EC' : 'background.paper',
      }}
    >
      <Stack direction="row" spacing={1.25} alignItems="center">
        <Typography sx={{ width: 54, flexShrink: 0, fontWeight: 900, color: statusColor(occurrence) }}>{formatTime(occurrence)}</Typography>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{ fontWeight: 800 }}>{medication.name || 'Medication'}{dose ? ` ${dose}` : ''}</Typography>
          <Typography variant="body2" color="text.secondary">{occurrence.roundName || 'Medication reminder'}</Typography>
          {occurrence.status === 'not_taken' ? <Typography variant="body2">{MEDICATION_NOT_TAKEN_REASONS.find(([value]) => value === occurrence.reason)?.[1] || occurrence.reason}</Typography> : null}
        </Box>
        <Typography aria-label={statusLabel(occurrence)} sx={{ flexShrink: 0, fontWeight: 850, color: statusColor(occurrence) }}>{statusLabel(occurrence)}</Typography>
      </Stack>

      {!resolved ? (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={0.8} sx={{ mt: 1 }}>
          <Button variant="contained" disabled={busy} onClick={() => onRecord(occurrence.id, { status: 'taken' })} sx={{ minHeight: 44 }}>Taken</Button>
          <Button variant="outlined" disabled={busy} onClick={() => setShowNotTaken((value) => !value)} sx={{ minHeight: 44 }}>Not taken</Button>
        </Stack>
      ) : null}

      {showNotTaken && !resolved ? (
        <Stack spacing={1} sx={{ mt: 1 }}>
          <TextField select size="small" label="Reason" value={reason} onChange={(event) => setReason(event.target.value)}>
            {MEDICATION_NOT_TAKEN_REASONS.map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
          </TextField>
          <TextField size="small" label="Short note (optional)" value={note} onChange={(event) => setNote(event.target.value)} inputProps={{ maxLength: 500 }} />
          <Button variant="contained" color="warning" disabled={busy || !reason} onClick={() => onRecord(occurrence.id, { status: 'not_taken', reason, note })} sx={{ minHeight: 44, alignSelf: 'flex-start' }}>
            Record not taken
          </Button>
        </Stack>
      ) : null}
    </Box>
  );
};

const DailyMedicationCheck = ({ occurrences, focusedOccurrenceId, onRecord, busyId, error, success }) => {
  const groups = {
    taken: occurrences.filter((item) => item.status === 'taken'),
    needs_confirmation: occurrences.filter((item) => !isMedicationAdministrationResolved(item.status)),
    missed: occurrences.filter((item) => item.status === 'missed'),
    not_taken: occurrences.filter((item) => item.status === 'not_taken'),
  };
  const sections = [
    ['taken', 'Taken'],
    ['needs_confirmation', 'Needs confirmation'],
    ['missed', 'Missed'],
    ['not_taken', 'Not taken'],
  ];

  return (
    <Stack spacing={1.2}>
      <Stack direction="row" justifyContent="space-between" alignItems="baseline">
        <Typography sx={{ color: '#85928D', fontSize: '0.78rem', fontWeight: 900, letterSpacing: '0.08em' }}>TODAY</Typography>
        {occurrences.length ? <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 700 }}>
          {occurrences.length} scheduled
        </Typography> : null}
      </Stack>
      {error ? <Alert severity="error">{error}</Alert> : null}
      {success ? <Alert severity="success">{success}</Alert> : null}
      {occurrences.length === 0 ? <Typography variant="body2">No scheduled medication occurrences for today yet.</Typography> : null}
      {sections.map(([key, label]) => groups[key].length ? (
        <Box key={key}>
          <Typography variant="body2" sx={{ mb: 0.6, color: 'text.secondary', fontWeight: 800 }}>
            {label} — {groups[key].length}
          </Typography>
          <Stack spacing={0.8}>
            {groups[key].map((occurrence) => (
              <MedicationOccurrenceRow
                key={occurrence.id}
                occurrence={occurrence}
                focused={focusedOccurrenceId === occurrence.id}
                onRecord={onRecord}
                busy={busyId === occurrence.id}
              />
            ))}
          </Stack>
        </Box>
      ) : null)}
    </Stack>
  );
};

export default DailyMedicationCheck;
